import { DateTime } from 'luxon';
import { AxiosStatusCode } from 'smg/axios';
import * as buttons from 'smg/buttons';
import { Issuer } from 'smg/cc';
import { OpenOrderInvoice } from 'smg/customer-api/v2/orders';
import * as models from 'smg/customer-api/models';
import * as orders from 'smg/customer-api/orders';
import { ShowError } from 'smg/errors';
import * as html from 'smg/html'; // ^^
import QueryString from 'smg/querystring';
import { DollarFormat } from 'smg/util';
import { AccountReady } from '../../account-session';
import * as carts from '../../carts';
import { Relref } from '../../hugo';
import * as lineitems from '../../lineitems';
import * as payments from '../../payments';

const billingInformation = document.querySelector('#billingInformation') as HTMLParagraphElement;
const deliveryInformation = document.querySelector('#deliveryInformation') as HTMLParagraphElement;
const paymentType = document.querySelector('#paymentType') as HTMLSpanElement;
const creditCardLast4 = document.querySelector('#creditCardLast4') as HTMLSpanElement;
const thumbnailContainer = document.querySelector('#thumbnailContainer') as HTMLDivElement;
const invoiceContainer = document.querySelector('#invoiceContainer') as HTMLDivElement;
const spinner = document.querySelector('#spinner') as HTMLDivElement;
const content = document.querySelector('#content') as HTMLDivElement;
const invoiceNumber = document.querySelector('#invoiceNumber') as HTMLSpanElement;
const orderDate = document.querySelector('#orderDate') as HTMLSpanElement;
const balanceContainer = document.querySelector('#balanceContainer') as HTMLDivElement;
const balanceDue = document.querySelector('#balanceDue') as HTMLSpanElement;
const paymentIssuer = document.querySelector('#paymentIssuer') as HTMLSpanElement;

let counter = 0;

function stopSpinner() {
    spinner.classList.add('d-none');
}

function reveal(): void {
    content.classList.remove('d-none');
}

declare interface myWindow extends Window {
    readonly cloudFront: string;
}
declare const window: myWindow;

void main();

async function main() {
    const $notFound = document.querySelector('#order-not-found') as HTMLDivElement;
    const $notFoundHint = document.querySelector('#order-not-found-hint') as HTMLSpanElement;

    const showNotFound = (hint: string) => {
        stopSpinner();
        $notFoundHint.textContent = hint;
        $notFound.classList.remove('d-none');
    };

    if (await AccountReady) {
        return;
    }

    const key = QueryString().get('id');
    if (!key) {
        showNotFound('Missing order ID in URL');
        return;
    }

    let order: models.HyejooOrder;
    try {
        const data = await orders.GetOrder(key, {
            data: 'id,key,invoiceNumber,created,total,balance,pending,markedAsShipped,orderLineItems,orderProofs,orderPayments,orderShippingOptions',
        });
        if (!data) {
            showNotFound("We couldn't find an order matching that link on your account.");
            return;
        }
        order = data;
    } catch (err) {
        if (AxiosStatusCode(err) === 404) {
            showNotFound("We couldn't find an order matching that link on your account.");
            return;
        }
        stopSpinner();
        ShowError(err);
        throw err;
    }

    try {
        await render(order);
    } catch (err) {
        stopSpinner();
        ShowError(err);
        throw err;
    }
}

async function render(order: models.HyejooOrder) {
    // Add Invoice Number
    if (order.invoiceNumber) {
        invoiceNumber.innerText = order.invoiceNumber.toString();
    }

    // Add Date Created
    if (order.created.isValid) {
        orderDate.innerText = order.created.toLocaleString(DateTime.DATE_SHORT);
    }

    // Check For Balance Due
    if (order.balance.gt(0)) {
        balanceContainer.classList.remove('d-none');
        balanceDue.innerText = DollarFormat(order.balance);
    }

    // Make a Payment
    const makePayment = document.querySelector('#makePayment') as HTMLButtonElement;
    const balance = order.balance;
    makePayment.addEventListener('click', () => payments.PromptPayment(undefined, [order.id], balance).then(() => window.location.reload()));

    // If there are order proofs, capture that array and select all of the proof url's
    if (order.orderProofs.length) {
        const proofs = order.orderProofs;

        const proofUrls: string[] = [];
        const proofThumbnails: string[] = [];

        proofs.forEach(index => {
            if (index.proof?.main) {
                proofUrls.push(index.proof.main);
            }

            if (index.proof?.thumbnail) {
                proofThumbnails.push(index.proof.thumbnail);
            }
        });

        for (const everyThumbnail of proofThumbnails) {
            const thumbnail = document.createElement('div');
            const imgLink = document.createElement('a');
            const img = document.createElement('img');
            thumbnailContainer.append(thumbnail);
            thumbnail.classList.add('col', 'mb-5', 'mb-sm-none', 'me-sm-5');
            thumbnail.append(imgLink);
            imgLink.href = proofUrls[counter];
            imgLink.setAttribute('data-fancybox', 'gallery');
            imgLink.append(img);
            img.src = everyThumbnail;
            counter += 1; // Separate counter variable. There should be an equal amount of indexes for the thumbnails and the url's
        }
    }

    // Add table
    if (order.orderLineItems.length) {
        invoiceContainer.append(await lineitems.Table(order.orderLineItems));
        const table = document.querySelector('table') as HTMLTableElement;
        table.classList.add('table-dark');
    }

    // Add billing information details
    if (order.orderPayments.length) {
        const paymentOption = order.orderPayments[0].paymentOption;
        if (paymentOption) {
            billingInformation.innerHTML = html.Nl2br(html.Escape(paymentOption.address.toStringWithoutContact()));
        }
    }

    // Add Delivery Information
    if (order.orderShippingOptions.length) {
        const shippingOption = order.orderShippingOptions[0].shippingOption;
        if (shippingOption) {
            deliveryInformation.innerHTML = html.Nl2br(html.Escape(shippingOption.address?.toStringWithoutContact() ?? ''));
        }
    }

    // Add order payment details
    if (order.orderPayments.length) {
        const paymentOption = order.orderPayments[0].paymentOption;
        const cc = paymentOption?.creditCardFirst6?.toString() || '';
        paymentIssuer.innerText = Issuer(cc);
        paymentType.innerText = paymentOption?.paymentOptionType?.name || '';
        if (!paymentOption?.creditCardLast4) {
            creditCardLast4.classList.add('d-none');
        } else {
            creditCardLast4.innerText = `ending in ${paymentOption.creditCardLast4.toString()}`;
        }
    }

    // Add Reorder
    const reorder = document.querySelector('#reorder') as HTMLButtonElement;
    // $0 reorders are rejected server-side (#7049), so hide the button instead of wiring a dead click handler
    if (order.total.eq(0)) {
        reorder.classList.add('d-none');
    } else {
        reorder.addEventListener('click', () => {
            buttons.Start(reorder);
            void carts.AddCartItem({ orderID: order.key }).then(() => {
                reorder.classList.remove('btn-primary', 'btn-success', 'text-white');
                reorder.classList.add('btn-outline-success');

                const $icon = reorder.querySelector('.material-symbols-sharp');
                if ($icon) {
                    $icon.textContent = 'check';
                }
            }).finally(() => buttons.Stop(reorder));
        });
    }

    // Add View Invoice
    const viewInvoice = document.querySelector('#viewInvoice') as HTMLButtonElement;
    viewInvoice.onclick = () => {
        buttons.Start(viewInvoice);
        void OpenOrderInvoice(`id:${order.id}`).catch(ShowError).finally(() => buttons.Stop(viewInvoice));
    };

    // Cancel Order — only when not marked shipped; server re-checks voidability.
    const $cancelOrder = document.querySelector<HTMLButtonElement>('#cancelOrder');
    if ($cancelOrder && orders.OrderIsCancelable(order)) {
        $cancelOrder.classList.remove('d-none');
        $cancelOrder.addEventListener('click', () => {
            void orders.PromptCancelOrder(order.id, {
                redirectTo: Relref('/orders'),
            });
        });
    }

    stopSpinner();
    reveal();
}
